'use client';

import Link from 'next/link';
import { useEffect, useId, useRef, useState } from 'react';
import { COOKIE_POLICY_PATH } from '@/constants/legalRoutes';
import { useConsent } from './ConsentProvider';

export default function CookiePreferences() {
  const {
    preferencesOpen,
    closePreferences,
    state,
    acceptAll,
    rejectAll,
    saveCustom,
  } = useConsent();
  const titleId = useId();
  const descId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const [externalContent, setExternalContent] = useState(
    state.choices.externalContent,
  );

  useEffect(() => {
    if (preferencesOpen) {
      setExternalContent(state.choices.externalContent);
    }
  }, [preferencesOpen, state.choices.externalContent]);

  useEffect(() => {
    if (!preferencesOpen) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    const focusable = dialog?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePreferences();
        return;
      }
      if (event.key !== 'Tab' || !dialog || !focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [preferencesOpen, closePreferences]);

  if (!preferencesOpen) {
    return null;
  }

  return (
    <div className="asst-cookie-prefs" role="presentation">
      <button
        type="button"
        className="asst-cookie-prefs__backdrop"
        aria-label="Chiudi preferenze cookie"
        onClick={closePreferences}
      />
      <div
        ref={dialogRef}
        className="asst-cookie-prefs__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <div className="asst-cookie-prefs__header">
          <h2 id={titleId} className="h4 mb-0">
            Preferenze cookie
          </h2>
          <button
            type="button"
            className="btn-close"
            aria-label="Chiudi"
            onClick={closePreferences}
          />
        </div>

        <p id={descId} className="asst-cookie-prefs__intro">
          Puoi scegliere quali categorie attivare. I cookie tecnici necessari sono
          sempre attivi. Maggiori dettagli nella{' '}
          <Link prefetch={false} href={COOKIE_POLICY_PATH} onClick={closePreferences}>
            Cookie policy
          </Link>
          .
        </p>

        <div className="asst-cookie-prefs__categories">
          <div className="form-check">
            <input
              type="checkbox"
              id="consent-necessary"
              checked
              disabled
              readOnly
            />
            <label className="form-check-label" htmlFor="consent-necessary">
              <strong>Necessari</strong>
              <span className="d-block text-muted small">
                Cookie tecnici per autenticazione, sicurezza e memorizzazione delle
                tue preferenze cookie. Non possono essere disattivati.
              </span>
            </label>
          </div>

          <div className="form-check mt-3">
            <input
              type="checkbox"
              id="consent-external"
              checked={externalContent}
              onChange={(event) => setExternalContent(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="consent-external">
              <strong>Contenuti di terze parti</strong>
              <span className="d-block text-muted small">
                Video (YouTube, Vimeo) e mappe (OpenStreetMap). Senza consenso viene
                mostrato un segnaposto locale.
              </span>
            </label>
          </div>
        </div>

        <div className="asst-cookie-prefs__actions">
          <button type="button" className="btn btn-outline-primary" onClick={rejectAll}>
            Rifiuta tutti
          </button>
          <button type="button" className="btn btn-outline-primary" onClick={acceptAll}>
            Accetta tutti
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => saveCustom({ externalContent })}
          >
            Salva preferenze
          </button>
        </div>
      </div>
    </div>
  );
}
